import React from 'react';
import classNames from 'classnames';
import Text from '../text/text';

// PR-SPIN.4: unify every loading surface onto the single branded Forex Lab
// cube spinner. This component is imported as `import { Loading } from
// '@deriv/components'` by ~15 call sites (routes-config.js, binary-routes.jsx,
// account-switcher, account-signup-modal, real-account-signup-loader,
// ResetOrUnlinkPasswordModal, UpdateEmailModal, ComplaintsPolicy, add-currency,
// choose-currency, set-currency, reset-or-unlink-password-modal, the bot-web-ui
// lazy-loader + BlocklyLoading, etc.). Previous passes (PR-SPIN.3) used a 3-dot
// spinner here — leaving the user looking at TWO different loaders during boot
// (HTML cube → dots). PR-SPIN.4 renders the same cube (3 pulsing halos + cube
// `<picture>`) as `index.html` / `templates/app/components/loading.jsx`, at a
// compact ~64px size so it works fullscreen AND inside modals.
//
// CSS is inlined so the spinner renders before the components SCSS bundle
// finishes loading. The cube image uses absolute `/public/...` paths so it
// resolves from any package. Props (TLoadingProps) + default export unchanged.
export type TLoadingProps = React.HTMLProps<HTMLDivElement> & {
    is_fullscreen: boolean;
    is_slow_loading: boolean;
    status: string[];
    theme: string;
};

const SPINNER_CSS = `
.fx-loading-cube {
    position: relative;
    width: 64px;
    height: 64px;
    display: grid;
    place-items: center;
    margin: 2rem auto;
}
.fx-loading-cube__halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 1px solid #7c3aed;
    opacity: 0;
    animation: fx-loading-halo-pulse 1.5s cubic-bezier(0.22, 0.61, 0.36, 1) infinite;
}
.fx-loading-cube__halo--2 {
    animation-delay: 0.5s;
    border-color: #3b82f6;
}
.fx-loading-cube__halo--3 {
    animation-delay: 1s;
    border-color: #d946ef;
}
.fx-loading-cube__wrap {
    width: 60%;
    height: 60%;
    position: relative;
    z-index: 1;
    animation: fx-loading-cube-bob 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
    transform-origin: 50% 60%;
}
.fx-loading-cube__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    animation: fx-loading-cube-squash 1.5s cubic-bezier(0.34, 1.56, 0.64, 1) infinite;
    transform-origin: 50% 100%;
    filter: drop-shadow(0 6px 14px rgba(91, 33, 182, 0.55));
}
@keyframes fx-loading-halo-pulse {
    0%   { transform: scale(0.55); opacity: 0.7; }
    100% { transform: scale(1.20); opacity: 0; }
}
@keyframes fx-loading-cube-bob {
    0%   { transform: translateY(-14%); }
    32%  { transform: translateY(6%); }
    55%  { transform: translateY(-4%); }
    75%  { transform: translateY(2%); }
    100% { transform: translateY(-14%); }
}
@keyframes fx-loading-cube-squash {
    0%, 30% { transform: scale(1, 1); }
    34%     { transform: scale(1.18, 0.82); }
    44%     { transform: scale(0.92, 1.10); }
    60%     { transform: scale(1.04, 0.96); }
    100%    { transform: scale(1, 1); }
}
@media (prefers-reduced-motion: reduce) {
    .fx-loading-cube__halo,
    .fx-loading-cube__wrap,
    .fx-loading-cube__img {
        animation: none !important;
    }
    .fx-loading-cube__halo { opacity: 0.3; }
}
`;

const Loading = ({ className, id, is_fullscreen = true, is_slow_loading, status }: Partial<TLoadingProps>) => {
    return (
        <div
            data-testid='dt_initial_loader'
            className={classNames(
                'initial-loader',
                {
                    'initial-loader--fullscreen': is_fullscreen,
                },
                className
            )}
        >
            <style>{SPINNER_CSS}</style>
            <div id={id} className='fx-loading-cube' role='status' aria-label='Loading'>
                <span className='fx-loading-cube__halo' />
                <span className='fx-loading-cube__halo fx-loading-cube__halo--2' />
                <span className='fx-loading-cube__halo fx-loading-cube__halo--3' />
                <span className='fx-loading-cube__wrap'>
                    <picture>
                        <source srcSet='/public/images/forex-lab/spinner/cube-192.webp' type='image/webp' />
                        <img
                            src='/public/images/forex-lab/spinner/cube-256.png'
                            alt=''
                            className='fx-loading-cube__img'
                            width='38'
                            height='38'
                            decoding='async'
                        />
                    </picture>
                </span>
            </div>
            {is_slow_loading &&
                status?.map((text, inx) => (
                    <Text as='h3' color='prominent' size='xs' align='center' key={inx}>
                        {text}
                    </Text>
                ))}
        </div>
    );
};

export default Loading;
